import StudentAvatarUrl from '@/assets/defaultavatar3.png'
import VolunteerAvatarUrl from '@/assets/defaultavatar4.png'
import SessionService from '@/services/SessionService'
import UserService from '@/services/UserService'
import NetworkService from '@/services/NetworkService'
import LoggerService from '@/services/LoggerService'
import Case from 'case'
import _ from 'lodash'
import i18n from '@/i18n'

export default {
  namespaced: true,
  state: {
    user: {},
    session: {},
    latestSession: {},
    recapSession: {},
    isFirstDashboardVisit: false,
    isSessionConnectionAlive: false,
    presessionSurvey: {},
    unreadChatMessageIndices: [],
    chatScrolledToMessageIndex: null,
    hadASession: false,
    prevSessionSubject: '',
  },
  mutations: {
    setUser: (state, user = {}) => (state.user = user),

    updateUser: (state, user = {}) => {
      // Task 11: ordering is only meaningful when BOTH timestamps are real
      // Date instances with a finite `getTime()`. A missing or unparseable
      // timestamp on either side must never freeze the store -- it must
      // degrade to "accept this update", not "reject every update forever".
      // (An Invalid Date object -- `new Date(NaN)` -- is truthy, so the old
      // `!user.date` check could never catch this case; that gap is exactly
      // what silently froze the store after the first successful load.)
      const incoming = user.date instanceof Date ? user.date.getTime() : NaN
      const current =
        state.user.date instanceof Date ? state.user.date.getTime() : NaN
      const bothValid = Number.isFinite(incoming) && Number.isFinite(current)

      if (!bothValid || incoming >= current) {
        state.user = user
      }
    },

    setSession: (state, session = {}) => (state.session = session),

    setRecapSession: (state, session = {}) => (state.recapSession = session),

    setLatestSession: (state, session = {}) => (state.latestSession = session),

    addMessage: (state, message) => {
      if (message) state.session.messages.push(message)
    },

    addRecapMessage: (state, message) => {
      if (message) state.recapSession.messages.push(message)
    },

    setAvailability: (state, availability, date) => {
      if (availability) {
        state.user.availability = availability
        state.user.date = date
      }
    },

    setTimezone: (state, timezone, date) => {
      if (timezone) {
        state.user.timezone = timezone
        state.user.date = date
      }
    },

    setIsFirstDashboardVisit: (state, isFirstDashboardVisit) => {
      state.isFirstDashboardVisit = isFirstDashboardVisit
    },

    setIsSessionConnectionAlive: (state, isSessionConnectionAlive) => {
      state.isSessionConnectionAlive = isSessionConnectionAlive
    },

    setPresessionSurvey: (state, presessionSurvey = {}) =>
      (state.presessionSurvey = presessionSurvey),

    markChatMessageUnread: (state, index) => {
      if (typeof index === 'number') {
        state.unreadChatMessageIndices.push(index)
      }
    },

    markChatMessageRead: (state, index) => {
      if (typeof index === 'number') {
        state.unreadChatMessageIndices = state.unreadChatMessageIndices.filter(
          v => v !== index
        )
      }
    },

    markChatMessagesRead: (state, indices) => {
      if (indices && indices.length > 0) {
        state.unreadChatMessageIndices = state.unreadChatMessageIndices.filter(
          v => indices.indexOf(v) === -1
        )
      }
    },

    clearUnreadChatMessages: state => {
      state.unreadChatMessageIndices = []
    },

    scrollChatToMessage: (state, index) => {
      if (typeof index === 'number') {
        state.chatScrolledToMessageIndex = index
      }
    },

    clearChatScrolledToMessageIndex: state => {
      state.chatScrolledToMessageIndex = null
    },

    setHadASession: (state, flag) => {
      state.hadASession = flag
    },

    setPrevSessionSubject: (state, subject) => {
      state.prevSessionSubject = subject
    },
  },
  actions: {
    fetch: ({ dispatch }, context) => {
      dispatch('fetchUser')
      dispatch('fetchSession', context)
    },

    clear: ({ commit }) => {
      commit('setUser', {})
      commit('setSession', {})
    },

    fetchUser: ({ commit }) => {
      return UserService.getUser()
        .then(user => {
          commit('updateUser', user)
        })
        .catch(err => {
          // erase the user only if not authenticated
          if (err?.response?.status === 401) {
            commit('setUser', {})
          }
        })
    },

    clearUser: ({ commit }) => {
      commit('setUser', {})
    },

    fetchSession: ({ commit, state }) => {
      SessionService.getCurrentSession(state.user)
        .then(({ sessionData }) => {
          commit('setSession', sessionData)
        })
        .catch(err => {
          commit('setSession', {})
          LoggerService.noticeError(err)
        })
    },

    fetchLatestSession: ({ commit, state }) => {
      SessionService.getLatestSession(state.user)
        .then(({ sessionData }) => {
          commit('setLatestSession', sessionData)
        })
        .catch(err => {
          commit('setLatestSession', {})
          LoggerService.noticeError(err)
        })
    },

    fetchRecapSessionForDms: async ({ commit }, sessionId) => {
      SessionService.getRecapSessionForDms(sessionId)
        .then(({ sessionData }) => {
          commit('setRecapSession', sessionData)
        })
        .catch(err => {
          commit('setRecapSession', {})
          if (err.status !== 404) {
            LoggerService.noticeError(err)
          }
        })
    },

    clearSession: ({ commit }) => {
      commit('setSession', {})
    },

    sessionDisconnected: ({ commit }) => {
      commit('setIsSessionConnectionAlive', false)
      commit('clearUnreadChatMessages')
      commit('clearChatScrolledToMessageIndex')
    },

    sessionConnected: ({ commit }) => {
      commit('setIsSessionConnectionAlive', true)
    },

    updateSession: ({ commit }, sessionData) => {
      commit('setSession', sessionData)
    },

    updateAvailability: ({ commit }, availability, date = Date.now()) => {
      commit('setAvailability', availability, date)
    },

    updateTimezone: ({ commit }, timezone, date = Date.now()) => {
      commit('setTimezone', timezone, date)
    },

    addMessage: ({ commit }, message) => {
      commit('addMessage', message)
    },

    addRecapMessage: ({ commit }, message) => {
      commit('addRecapMessage', message)
    },

    firstDashboardVisit: ({ commit }, isFirstDashboardVisit) => {
      commit('setIsFirstDashboardVisit', isFirstDashboardVisit)
    },

    addToUser: ({ commit, state }, data) => {
      const { user } = state
      const updatedUser = { ...user, ...data }
      commit('updateUser', updatedUser)
    },

    updatePresessionSurvey: ({ commit }, surveyData) => {
      commit('setPresessionSurvey', surveyData)
    },

    clearPresessionSurvey: ({ commit }) => {
      commit('setPresessionSurvey', {})
    },

    markChatMessageAsUnread: ({ commit }, index) => {
      commit('markChatMessageUnread', index)
    },

    markChatMessageAsRead: ({ commit }, index) => {
      commit('markChatMessageRead', index)
    },

    markChatMessagesAsRead: ({ commit }, indices) => {
      commit('markChatMessagesRead', indices)
    },

    scrollChatToMessage: ({ commit }, index) => {
      commit('scrollChatToMessage', index)
    },

    clearChatScrolledToMessageIndex: ({ commit }) => {
      commit('clearChatScrolledToMessageIndex')
    },

    updateHadASession: ({ commit }, flag) => {
      commit('setHadASession', flag)
    },

    updatePrevSessionSubject: ({ commit }, subject) => {
      commit('setPrevSessionSubject', subject)
    },

    // Task 11 / STEP-12 (Q-011): switch acting context only. Identity, roles,
    // profiles and authentication are all untouched -- the server verifies the
    // role is one the user actually holds and stores it on the session.
    setActiveRole: async ({ commit, state }, role) => {
      const { activeRole } = await NetworkService.setActiveRole(role).then(
        res => res.data
      )
      commit('updateUser', {
        ...state.user,
        activeRole,
        roleSelectionRequired: false,
      })
      return activeRole
    },

    // Task 11 / STEP-12 (Q-011): attach a role to the SAME account. On success
    // the server has already made the new role the acting context, so the
    // fresh roles/activeRole it returns are committed directly rather than
    // guessed at locally.
    onboardRole: async ({ commit, state }, { role, data }) => {
      const payload =
        role === 'volunteer'
          ? await NetworkService.onboardVolunteerRole(data).then(r => r.data)
          : await NetworkService.onboardStudentRole(data).then(r => r.data)

      if (payload.activeRole) {
        commit('updateUser', {
          ...state.user,
          roles: payload.roles,
          activeRole: payload.activeRole,
          roleSelectionRequired: false,
        })
      }
      return payload
    },

    // Task 11: authoritative reconciliation after role onboarding.
    //
    // The optimistic commit above is not a sufficient basis for navigating,
    // because the very next thing that happens is a route guard
    // (router.js:223) reading `isAutoFlowUser` -> `isVolunteer` ->
    // `activeRole`. If the client's acting role is wrong or merely stale at
    // that instant, a user who just onboarded Student is redirected to
    // /welcome and shown the Volunteer qualification flow -- the manual
    // failure this exists to prevent.
    //
    // So: re-read the server's own view first, and only then let navigation
    // proceed. `fetchUser` is AWAITED here specifically because the router's
    // own `getUser()` helper does not await it.
    //
    // If the reconciled acting role still disagrees with the role just
    // onboarded, it is corrected through the server (`setActiveRole`) rather
    // than by writing the client store, so the fix is durable across the next
    // fetch instead of being overwritten by it.
    reconcileAfterRoleOnboarding: async ({ dispatch, getters }, role) => {
      await dispatch('fetchUser')

      if (getters.activeRole !== role && getters.roles.includes(role)) {
        await dispatch('setActiveRole', role)
      }

      return getters.activeRole
    },
  },
  getters: {
    // Task 11 / STEP-12: acting context -- a dual-role user acting as a
    // student sees the student avatar and student fallback name.
    avatarUrl: (state, getters) =>
      state.user.picture ||
      (getters.isVolunteer ? VolunteerAvatarUrl : StudentAvatarUrl),

    firstName: (state, getters) =>
      state.user.firstname || (getters.isVolunteer ? 'Volunteer' : 'Student'),
    lastName: state => state.user.lastname,
    fullName: (state, getters) =>
      [getters.firstName, getters.lastName].join(' '),

    // Task 11 / STEP-12 (Q-011): role MEMBERSHIP -- what this identity is
    // permitted to be. Authoritative, sourced from users_roles server-side.
    roles: state => state.user.roles || [],
    hasStudentRole: (state, getters) => getters.roles.includes('student'),
    hasVolunteerRole: (state, getters) => getters.roles.includes('volunteer'),

    // Acting context -- what this session is doing right now. Null for a
    // dual-role user who has not chosen yet.
    activeRole: state => state.user.activeRole || null,
    roleSelectionRequired: state => !!state.user.roleSelectionRequired,
    isDualRole: (state, getters) =>
      getters.hasStudentRole && getters.hasVolunteerRole,

    // Task 11 / STEP-12 (Q-011): LEGACY COMPATIBILITY ALIAS, and it aliases
    // ACTING CONTEXT, not membership. This is the single most important line
    // in the frontend role model, so it is worth being explicit about why:
    //
    // ~123 call sites read `isVolunteer`. Almost all of them ask "is this user
    // currently behaving as a volunteer" -- which dashboard to render, which
    // avatar, which side of a session they are on. If this aliased membership
    // (`hasVolunteerRole`) instead, then the moment a student attached a
    // volunteer role, every one of those call sites would flip to volunteer
    // behaviour while the person was still acting as a student.
    //
    // Consumers that genuinely mean membership must use `hasVolunteerRole` /
    // `hasStudentRole` explicitly. `state.user.isVolunteer` (the server's
    // profile-derived membership field) is deliberately NOT read here.
    isVolunteer: (state, getters) => getters.activeRole === 'volunteer',
    isAdmin: state => state.user.isAdmin,

    isAuthenticated: state => !!(state.user && state.user._id),

    isVerified: state => state.user.verified,

    hasCertification: state => {
      // UPchieve 101 is a training course and not technically considered
      // a certification. It's nested in user.certifications for legacy purposes
      const certs = Object.assign({}, state.user.certifications)
      delete certs.upchieve101
      return _.some(certs, { passed: true })
    },

    passedUpchieve101: state => {
      return state.user.certifications.upchieve101.passed
    },

    hasSelectedAvailability: state => !!state.user.availabilityLastModifiedAt,

    sessionPath: state => {
      const { type, subTopic, _id } = state.session
      const path = `/${i18n.locale}/session/${Case.kebab(type)}/${Case.kebab(
        subTopic
      )}/${_id}`

      return path
    },

    sessionPartner: (state, getters) => {
      if (
        typeof state.session.volunteer !== 'object' ||
        typeof state.session.student !== 'object'
      ) {
        return {}
      }

      if (getters.isVolunteer) {
        return state.session.student
      } else {
        return state.session.volunteer
      }
    },

    isSessionAlive: state => {
      // Early exit if the session doesn't exist
      if (!state.session.createdAt) {
        return false
      }

      // True if the session hasn't ended
      return !state.session.endedAt
    },

    isSessionWaitingForVolunteer: state => {
      // Early exit if the session doesn't exist or has ended
      if (!state.session.createdAt || !!state.session.endedAt) {
        return false
      }

      // True if volunteer hasn't joined
      return !state.session.volunteerJoinedAt
    },

    isSessionInProgress: state => {
      // Early exit if the session doesn't exist
      if (!state.session.createdAt) {
        return false
      }

      // True if volunteer has joined and the session hasn't ended
      return !!state.session.volunteerJoinedAt && !state.session.endedAt
    },

    isSessionOver: state => {
      // Early exit if the session doesn't exist
      if (!state.session.createdAt) {
        return false
      }

      // True if the session has ended
      return !!state.session.endedAt
    },

    numberOfUnreadChatMessages: state => {
      return state.unreadChatMessageIndices.length
    },

    isAutoFlowUser: (state, getters) => {
      return (
        !state.user.isOnboarded &&
        getters.isVolunteer &&
        !getters.hasCertification
      )
    },

    isQuizStudyMaterialUser: (state, getters, rootState, rootGetters) => {
      return (
        !state.user.isOnboarded &&
        getters.isVolunteer &&
        !getters.hasCertification &&
        rootGetters['featureFlags/isQuizStudyMaterialsActive']
      )
    },

    showDashboardRedesign: (state, _getters, _rootState, rootGetters) => {
      return (
        // Task 11 / STEP-12: acting context -- this is a student-dashboard
        // experiment, so it follows what the user is doing, not what they are.
        _getters.activeRole !== 'volunteer' &&
        (rootGetters['featureFlags/showDashboardRedesign'] ||
          state.user.studentPartnerOrg ===
            rootGetters['featureFlags/forceShowDashboardRedesignOrg'])
      )
    },

    getUserPropsForAnalytics: (state, getters) => {
      const userProps = {
        ucId: state.user.id,
        userType: state.user.type,
        createdAt: state.user.createdAt,
        totalSessions: state.user.pastSessions.length,
        isBanned: state.user.isBanned,
        isTestUser: state.user.isTestUser,
      }
      if (getters.isVolunteer) {
        userProps.onboarded = state.user.isOnboarded
        userProps.approved = state.user.isApproved
        userProps.partner = state.user.volunteerPartnerOrg

        const certificationInfo = Object.entries(
          state.user.certifications
        ).reduce((acc, [subject, quizInfo]) => {
          acc[subject] = quizInfo.passed
          return acc
        }, {})
        return {
          ...userProps,
          ...certificationInfo,
        }
      } else {
        userProps.partner = state.user.studentPartnerOrg
        userProps.gradeLevel = state.user.gradeLevel
        if (state.user.isSchoolPartner) {
          userProps.schoolPartner = state.user.school
        }
      }

      return userProps
    },
  },
}
